import React, { useMemo, useRef, useState } from 'react';
import dayjs from "dayjs";
import CountrySelect from "../../shared/components/common/CountrySelect";
import DaySelect from "../../shared/components/date/DaySelect";
import {Select, InputNumber, Alert, Space, Typography, Splitter} from "antd";
import { PACKAGE_MANAGER_TYPE } from "../../shared/utils/enum";
import EChartsCohortChart from "./heatmap/EChartsCohortChart";
import TablePage from "../../pages/tablePage/TablePage";
import { PackageManagerEntity } from "../storeManager/PackageManagerEntity";
import PackageManagerEditModal from "../storeManager/PackageManagerEditModal";
import { usePackageDataTable } from "../probability/useItemData";
import { usePackageStatistics } from './usePackageStatistics';

const { Text } = Typography;

const PLATFORM_OPTIONS = [
    {label: 'All', value: 'all'},
    {label: 'Android', value: 'google'},
    {label: 'iOS', value: 'apple'},
];

const FIRST_PURCHASE_OPTIONS = [
    {label: 'All', value: 'all'},
    {label: 'True', value: 1},
    {label: 'False', value: 0},
];

const DEFAULT_MIN_LEVEL = 4;
const DEFAULT_MAX_LEVEL = 300;

const PackageStatistics = () => {
    const [customDate, setCustomDate] = useState(dayjs().endOf('day'));
    const [platform, setPlatform] = useState('all');
    const [firstPurchase, setFirstPurchase] = useState('all');
    const [country, setCountry] = useState('ALL');
    const [minLevel, setMinLevel] = useState(4);
    const [maxLevel, setMaxLevel] = useState(DEFAULT_MAX_LEVEL);

    const [rowData, setRowData] = useState([]);

    const refreshRef = useRef(null);
    const {data: chartData, isLoading, error} = usePackageStatistics({
        date: customDate,
        platform,
        country,
        minLevel,
        maxLevel,
        firstPurchase
    });

    const {data: packageData, isLoading: isTableLoading, error: tableError} = usePackageDataTable();

    const columns = useMemo(() => [
        {
            title: '상품ID',
            dataIndex: 'packageId',
            key: 'packageId',
            width: 150,
            sorter: (a, b) => a.packageId - b.packageId,
            searcher: true,
        },
        {
            title: '상품명',
            dataIndex: 'packageName',
            key: 'packageName',
            width: 150,
        },
        {
            title: '가격',
            dataIndex: 'price',
            key: 'price',
            width: 100,
        },
        {
            title: '분류1',
            dataIndex: 'category1',
            key: 'category1',
        },
        {
            title: '분류2',
            dataIndex: 'category2',
            key: 'category2',
        },
    ], []);

    const handleSetCountry = (newValue) => {
        setCountry(newValue);
    }
    const handleChangePlatform = (value) => {
        setPlatform(value);
    };
    const handleSetLevel = (name, value) => {
        const numberValue = Number(value);
        if (name === 'minLevel') {
            setMinLevel(numberValue);
        } else if (name === 'maxLevel') {
            setMaxLevel(numberValue);
        }
    }
    const handleOnBlurCheck = (name, value) => {
        if (name === 'minLevel') {
            (value > DEFAULT_MAX_LEVEL || value < DEFAULT_MIN_LEVEL) && setMinLevel(DEFAULT_MIN_LEVEL);
        } else if (name === 'maxLevel') {
            (value > DEFAULT_MAX_LEVEL || value < DEFAULT_MIN_LEVEL) && setMaxLevel(DEFAULT_MAX_LEVEL);
        }
    }
    const handleChangeFirstPurchase = (value) => {
        setFirstPurchase(value);
    };

    const props = useMemo(() => ({
        columns,
        rowData,
        setRowData,
        refreshRef,
        modal: PackageManagerEditModal,
        buttonText: '상품 추가',
        localData: Object.values(packageData || {}),
        csvUpload: 'manager/analysis/package',
        entityModel: PackageManagerEntity,
        csvDownload: process.env.REACT_APP_URL + 'manager/analysis/package/download',
        csvGuide: ['상단 [CSV Download] 로 현재 상품 목록을 받아 수정한 뒤 업로드하세요.'],
    }), [columns, rowData, packageData]);

    if (isLoading || isTableLoading) return <div>데이터 로딩 중...</div>;
    const firstError = error || tableError;
    if (firstError) return <div>에러 발생: {firstError.message}</div>;

    return (
        <div>
            <Splitter style={{height: '100%'}}>
                <Splitter.Panel defaultSize="60%" min="30%" max="70%">
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginTop: '10px',
                        marginBottom: '10px',
                        justifyContent: 'flex-start'
                    }}>
                        <DaySelect date={customDate} setDate={setCustomDate}/>
                        <Select
                            value={platform}
                            onChange={handleChangePlatform}
                            style={{ width: 150, marginRight: 10 }}
                            size="middle"
                        >
                            {PLATFORM_OPTIONS.map((option) => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                        <CountrySelect country={country} setCountry={handleSetCountry}/>
                        <InputNumber
                            min={DEFAULT_MIN_LEVEL}
                            max={DEFAULT_MAX_LEVEL}
                            value={minLevel}
                            name="minLevel"
                            onChange={value => handleSetLevel('minLevel', value)}
                            onBlur={e => handleOnBlurCheck('minLevel', e.target.value)}
                            style={{ width: 150, marginRight: 10 }}
                            placeholder="플레이어 최소 레벨"
                        />
                        <InputNumber
                            min={DEFAULT_MIN_LEVEL}
                            max={DEFAULT_MAX_LEVEL}
                            value={maxLevel}
                            name="maxLevel"
                            onChange={value => handleSetLevel('maxLevel', value)}
                            onBlur={e => handleOnBlurCheck('maxLevel', e.target.value)}
                            style={{ width: 150, marginRight: 10 }}
                            placeholder="플레이어 최대 레벨"
                        />
                        <Select
                            value={firstPurchase}
                            onChange={handleChangeFirstPurchase}
                            style={{ width: 150, marginRight: 10 }}
                            size="middle"
                        >
                            {FIRST_PURCHASE_OPTIONS.map((option) => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <Alert
                            message="차트 사용 가이드"
                            description={
                                <Space direction="vertical" size="small">
                                    <Text style={{fontSize: '12px'}}>
                                        💡 히트맵의 각 셀은 특정 날짜와 카테고리의 교집합 데이터를 나타냅니다
                                    </Text>
                                    <Text style={{fontSize: '12px'}}>
                                        🎨 색상이 진할수록 높은 값을 의미하며, 셀 위에 마우스를 올리면 상세 정보를 확인할 수 있습니다
                                    </Text>
                                    <Text style={{fontSize: '12px'}}>
                                        📊 표시 모드를 변경하여 비율과 절대값을 전환할 수 있습니다
                                    </Text>
                                </Space>
                            }
                            type="info"
                            showIcon
                            style={{
                                marginTop: '16px',
                                marginBottom: '16px'
                            }}
                        />
                        <EChartsCohortChart
                            chartType={PACKAGE_MANAGER_TYPE.COUNT}
                            externalData={chartData}
                        />
                        <EChartsCohortChart
                            chartType={PACKAGE_MANAGER_TYPE.AMOUNT}
                            externalData={chartData}
                        />
                        <EChartsCohortChart
                            chartType={PACKAGE_MANAGER_TYPE.RANGE}
                            externalData={chartData}
                            category1Filter={'다이아'}
                        />
                        <EChartsCohortChart
                            chartType={PACKAGE_MANAGER_TYPE.LEVEL}
                            externalData={chartData}
                        />
                    </div>
                </Splitter.Panel>
                <Splitter.Panel>
                    <div>
                        <h3>패키지 데이터</h3>
                        <TablePage {...props}/>
                    </div>
                </Splitter.Panel>
            </Splitter>
        </div>
    );
};

export default PackageStatistics;